import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, FilePlus, FileImage, Folder, Upload } from 'lucide-react';
import { CertificateTemplateType, CertificateTemplate } from '@/types/certificateTemplate';
import { useCertificateTemplates } from '@/hooks/useCertificateTemplates';
import CertificateTemplateCard from './CertificateTemplateCard';
const CertificateTemplateLibrary = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<CertificateTemplateType>('course-completion');
  const {
    templates,
    isLoading,
    error,
    deleteTemplate,
    setDefaultTemplate,
    duplicateTemplate
  } = useCertificateTemplates(activeTab);
  const handleCreateNewTemplate = () => {
    navigate('/certificate-templates/new/basic-info');
  };
  const handleEditTemplate = (template: CertificateTemplate) => {
    navigate(`/certificate-templates/edit/${template.id}/basic-info`);
  };
  const handleDeleteTemplate = (templateId: string) => {
    deleteTemplate(templateId);
  };
  const handleDuplicateTemplate = (templateId: string) => {
    duplicateTemplate(templateId);
  };
  const handleSetDefaultTemplate = (templateId: string) => {
    setDefaultTemplate(templateId);
  };
  const getPdfTemplatesCount = () => {
    return templates.filter(t => t.metadata?.isPdf).length;
  };
  if (isLoading) {
    return <div className="py-10 text-center">Loading certificate templates...</div>;
  }
  if (error) {
    return <div className="py-10 text-center text-red-500">Error: {error.message}</div>;
  }
  return <div className="space-y-6 animate-fade-in">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-xl flex items-center">
                <Folder className="h-5 w-5 mr-2" />
                Certificate Template Library
              </CardTitle>
              <CardDescription>
                Manage certificate templates for different certificate types
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateNewTemplate}>
                <FilePlus className="h-4 w-4 mr-2" />
                Create New Template
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="course-completion" value={activeTab} onValueChange={value => setActiveTab(value as CertificateTemplateType)} className="w-full">
            <TabsList className="mb-4 rounded-xxxsm">
              <TabsTrigger value="course-completion" className="flex items-center rounded-sm text-left font-thin text-sm px-0 my-0 mx-px">
                <FileText className="h-4 w-4 mr-2" />
                Course Completion
              </TabsTrigger>
              <TabsTrigger value="enrollment" className="flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Enrollment
              </TabsTrigger>
              <TabsTrigger value="professional-development" className="flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Professional Development
              </TabsTrigger>
              <TabsTrigger value="custom" className="flex items-center">
                <FileImage className="h-4 w-4 mr-2" />
                Custom
              </TabsTrigger>
            </TabsList>
            
            {getPdfTemplatesCount() > 0 && <Alert className="mb-4 bg-blue-50 text-blue-800 border-blue-200">
                <AlertDescription className="flex items-center">
                  <Upload className="h-4 w-4 mr-2" />
                  {getPdfTemplatesCount()} PDF template{getPdfTemplatesCount() !== 1 ? 's' : ''} available. 
                  PDF templates allow you to overlay data on your custom PDF designs.
                </AlertDescription>
              </Alert>}
            
            <TabsContent value="course-completion" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => <CertificateTemplateCard key={template.id} template={template} onEdit={() => handleEditTemplate(template)} onDelete={() => handleDeleteTemplate(template.id)} onDuplicate={() => handleDuplicateTemplate(template.id)} onSetDefault={() => handleSetDefaultTemplate(template.id)} />)}
              </div>
            </TabsContent>
            
            <TabsContent value="enrollment" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => <CertificateTemplateCard key={template.id} template={template} onEdit={() => handleEditTemplate(template)} onDelete={() => handleDeleteTemplate(template.id)} onDuplicate={() => handleDuplicateTemplate(template.id)} onSetDefault={() => handleSetDefaultTemplate(template.id)} />)}
              </div>
            </TabsContent>
            
            <TabsContent value="professional-development" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => <CertificateTemplateCard key={template.id} template={template} onEdit={() => handleEditTemplate(template)} onDelete={() => handleDeleteTemplate(template.id)} onDuplicate={() => handleDuplicateTemplate(template.id)} onSetDefault={() => handleSetDefaultTemplate(template.id)} />)}
              </div>
            </TabsContent>
            
            <TabsContent value="custom" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => <CertificateTemplateCard key={template.id} template={template} onEdit={() => handleEditTemplate(template)} onDelete={() => handleDeleteTemplate(template.id)} onDuplicate={() => handleDuplicateTemplate(template.id)} onSetDefault={() => handleSetDefaultTemplate(template.id)} />)}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>;
};
export default CertificateTemplateLibrary;